import { useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { errorCode } from '../api/client'
import { useBooking, useExtendBooking } from '../api/queries'
import type { BookingDetails } from '../api/types'
import { CancelBookingDialog } from '../components/BookingDialog'
import { DeclineDialog } from '../components/DeclineDialog'
import { ErrorNotice } from '../components/ErrorNotice'
import { Badge, Button, Loading, PageTitle } from '../components/ui'
import { cn } from '../lib/cn'
import {
  dateKey,
  fmtDayTitle,
  fmtDuration,
  fmtRange,
  minutesBetween,
  parse,
} from '../lib/time'
import { useSession } from '../session'

/**
 * Страница одной встречи, экран 2.12.
 *
 * Существует прежде всего ради сценария 5: во всех письмах стоит «Открыть встречу»
 * со ссылкой на /meetings/{id}, и участник должен попасть именно на свою встречу,
 * а не на общее расписание, где ему предстоит её искать. Неавторизованного уводит
 * на вход RequireAuth — он же запоминает адрес, и после кода человек возвращается сюда.
 *
 * Порядок строк «Когда — Переговорная — Организатор» тот же, что в письмах: человек
 * приходит с письма и узнаёт ту же карточку, а не читает её заново.
 *
 * Права здесь — вторая ось: не «кто я в системе», а «кто я на этой встрече».
 * Организатор меняет и отменяет, участник говорит «не смогу прийти», посторонний
 * только смотрит. Флаги can_edit / can_cancel / can_extend считает сервер.
 */
export function MeetingPage() {
  const { bookingId: bookingIdParam } = useParams()
  const bookingId = Number(bookingIdParam)
  const idIsSane = Number.isFinite(bookingId) && bookingId > 0

  const { user } = useSession()
  const { data: booking, error, refetch } = useBooking(idIsSane ? bookingId : undefined)

  const extend = useExtendBooking()
  const [cancelling, setCancelling] = useState(false)
  const [declining, setDeclining] = useState(false)

  // Ссылка из письма живёт годами и переживает саму встречу: удалённая или
  // выдуманная бронь должна объяснять, что произошло, а не показывать пустоту.
  if (!idIsSane || errorCode(error) === 'not_found') return <NotFound />

  if (error) {
    return (
      <div className="mx-auto max-w-2xl">
        <PageTitle>Встреча</PageTitle>
        <ErrorNotice error={error} onRetry={() => void refetch()} />
      </div>
    )
  }

  if (!booking) return <Loading text="Открываем встречу…" />

  const role = booking.my_role ?? 'outsider'
  const participants = booking.participants ?? []
  const going = participants.filter((participant) => participant.status === 'invited')
  const me = participants.find((participant) => participant.user_id === user.id)
  const cancelled = booking.status === 'cancelled'
  const isKiosk = booking.source === 'kiosk'
  const now = new Date()
  const ongoing =
    !cancelled && parse(booking.starts_at) <= now && parse(booking.ends_at) > now

  return (
    <div className="mx-auto max-w-2xl">
      <PageTitle
        action={
          <Link to={`/?date=${dateKey(parse(booking.starts_at))}`}>
            <Button variant="ghost">К расписанию</Button>
          </Link>
        }
      >
        {booking.title || 'Занято'}
      </PageTitle>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <RoleBadge role={role} />
        {cancelled && <Badge tone="busy">Отменена</Badge>}
        {ongoing && <Badge tone="soon">Идёт сейчас</Badge>}
        {isKiosk && <Badge>Заняли с планшета</Badge>}
        {booking.recurrence && (
          <Badge tone="accent">{booking.recurrence.human_readable ?? 'Повторяющаяся серия'}</Badge>
        )}
      </div>

      {cancelled && (
        <div className="mb-4 rounded-card border border-state-busy/40 bg-state-busy/10 px-4 py-3">
          <p className="text-sm font-medium">Встреча отменена</p>
          <p className="mt-1 text-sm text-fg/80">
            {booking.cancel_reason
              ? `Причина: ${booking.cancel_reason}`
              : 'Комната снова свободна — если встреча всё-таки нужна, забронируйте её заново.'}
          </p>
        </div>
      )}

      <dl className="card grid gap-3 p-4 sm:grid-cols-[9rem_1fr]">
        <Row label="Когда">
          <span className="tnum">
            {fmtDayTitle(booking.starts_at)}, {fmtRange(booking.starts_at, booking.ends_at)} ·{' '}
            {fmtDuration(minutesBetween(booking.starts_at, booking.ends_at))}
          </span>
        </Row>
        <Row label="Переговорная">
          {booking.room ? (
            <Link to={`/rooms/${booking.room.id}?date=${dateKey(parse(booking.starts_at))}`} className="text-accent hover:underline">
              {booking.room.name}
            </Link>
          ) : (
            (booking.room_name ?? `Комната #${booking.room_id}`)
          )}
        </Row>
        <Row label="Организатор">
          {booking.organizer ? (
            <>
              {booking.organizer.display_name}
              {booking.organizer.position && (
                <span className="text-muted"> · {booking.organizer.position}</span>
              )}
              <span className="tnum block text-sm text-muted">{booking.organizer.email}</span>
            </>
          ) : (
            <span className="text-muted">
              Нет: комнату заняли прямо с планшета на двери.
            </span>
          )}
        </Row>
        <Row label={`Участники${participants.length > 0 ? `: ${going.length} из ${participants.length}` : ''}`}>
          {booking.participants_hidden ? (
            <span className="text-muted">
              Состав участников виден только тем, кто на встрече. С вопросами — к организатору,
              его контакты выше.
            </span>
          ) : participants.length === 0 ? (
            <span className="text-muted">Никого не звали.</span>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {participants.map((participant) => (
                <li
                  key={participant.user_id}
                  title={participant.email}
                  className={cn(
                    'rounded-lg border border-line px-2.5 py-1 text-sm',
                    participant.status === 'declined' && 'text-muted line-through',
                  )}
                >
                  {participant.display_name}
                </li>
              ))}
            </ul>
          )}
        </Row>
      </dl>

      {/* Экран 2.12: участник уже отметился. Кнопку не показываем второй раз —
          важнее сказать, что организатор об этом узнал. */}
      {me?.status === 'declined' && (
        <p className="mt-4 hint">
          Вы отметили, что не сможете прийти. Организатор получил письмо, из списка
          участников вы вычеркнуты. Бронь при этом остаётся: место занято под встречу.
        </p>
      )}

      {isKiosk && (
        <p className="mt-4 hint">
          У этой брони нет владельца. Освободить комнату можно только с планшета у двери:
          отсюда не видно, вышли из неё люди или нет.
        </p>
      )}

      {/* Действия по роли. can_edit / can_cancel / can_extend у отменённой встречи
          сервер уже отдаёт false, поэтому здесь остаётся один выход — «Все встречи»,
          и уводить его вместе с кнопками нельзя. */}
      <div className="mt-5 flex flex-wrap gap-2">
        {booking.can_extend && (
          <Button
            variant="primary"
            loading={extend.isPending}
            onClick={() => extend.mutate({ id: booking.id, minutes: 30 })}
          >
            + 30 минут
          </Button>
        )}

        {booking.can_edit && (
          <Link to={`/bookings/${booking.id}/edit`}>
            <Button variant={booking.can_extend ? 'ghost' : 'primary'}>Изменить</Button>
          </Link>
        )}

        {role === 'participant' && !cancelled && me?.status !== 'declined' && (
          <Button variant="ghost" onClick={() => setDeclining(true)}>
            Не смогу прийти
          </Button>
        )}

        {booking.can_cancel && (
          <Button variant="danger" onClick={() => setCancelling(true)}>
            Отменить встречу
          </Button>
        )}

        <Link to="/meetings">
          <Button variant="ghost">Все встречи</Button>
        </Link>
      </div>

      {extend.error && (
        <div className="mt-4">
          <ErrorNotice error={extend.error} />
        </div>
      )}

      {declining && <DeclineDialog booking={booking} onClose={() => setDeclining(false)} />}

      {cancelling && (
        <CancelBookingDialog
          booking={booking}
          requireReason={role !== 'organizer' && user.is_admin}
          onClose={() => setCancelling(false)}
          // Со страницы уходить некуда: после отмены она сама покажет «Встреча отменена»
          // с причиной — ровно то, что человек и пришёл сюда узнать.
          onCancelled={() => setCancelling(false)}
        />
      )}
    </div>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="label mb-0 self-start">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </>
  )
}

function RoleBadge({ role }: { role: NonNullable<BookingDetails['my_role']> }) {
  if (role === 'organizer') return <Badge tone="accent">Я организую</Badge>
  if (role === 'participant') return <Badge>Меня позвали</Badge>
  return <Badge>Чужая встреча</Badge>
}

/** Ссылка из письма ведёт на встречу, которой уже нет. Тупика тут быть не должно. */
function NotFound() {
  return (
    <div className="mx-auto max-w-2xl">
      <PageTitle>Встречу не нашли</PageTitle>
      <div className="card p-6">
        <p className="text-sm text-muted">
          Скорее всего, её отменили и удалили вместе с серией, а ссылка осталась в старом
          письме. Посмотрите список своих встреч — там видно всё, что есть, или выберите
          время заново.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/meetings">
            <Button variant="primary">Мои встречи</Button>
          </Link>
          <Link to="/">
            <Button variant="ghost">Расписание на день</Button>
          </Link>
        </div>
      </div>
    </div>
  )
}
